import { ChoreLength, ChorePriority, daysBetween, utcDateString, addUtcDays, type ChoreAssignment } from '@chore-project/shared';

/** Points awarded on completion: [priority][length]. Mirrors the API's table. */
const POINTS_TABLE: Record<ChorePriority, Record<ChoreLength, number>> = {
  [ChorePriority.MUST_DO]:   { [ChoreLength.SHORT]: 4, [ChoreLength.MEDIUM]: 5, [ChoreLength.LONG]: 7 },
  [ChorePriority.SHOULD_DO]: { [ChoreLength.SHORT]: 3, [ChoreLength.MEDIUM]: 4, [ChoreLength.LONG]: 6 },
  [ChorePriority.CAN_DO]:    { [ChoreLength.SHORT]: 2, [ChoreLength.MEDIUM]: 3, [ChoreLength.LONG]: 5 },
};

export function calcPoints(priority: ChorePriority, length: ChoreLength): number {
  return POINTS_TABLE[priority]?.[length] ?? 0;
}

/** Sum of points available from a set of assignments (their daily "max"). */
export function computeMax(assignments: ChoreAssignment[]): number {
  return assignments.reduce(
    (sum, a) => sum + calcPoints(a.priority ?? ChorePriority.SHOULD_DO, a.length ?? ChoreLength.MEDIUM),
    0,
  );
}

export interface WeeklyDay {
  date: string;
  points: number;
  maxPoints: number;
}

/**
 * Live length of a persisted completion streak, from the member's stored start
 * and last-active dates (UTC YYYY-MM-DD). Returns 0 once broken — i.e. the last
 * active day is earlier than yesterday. Today not-yet-done (last active =
 * yesterday) keeps the streak alive, matching how it's displayed.
 */
export function streakLength(startDate?: string, lastDate?: string, today: string = utcDateString()): number {
  if (!startDate || !lastDate) return 0;
  const yesterday = addUtcDays(today, -1);
  if (lastDate !== today && lastDate !== yesterday) return 0;
  return daysBetween(startDate, lastDate) + 1;
}

/** The daily goal is hit when there were points to earn and they're all earned. */
export function isGoalHit(earnedToday: number, maxToday: number): boolean {
  return maxToday > 0 && earnedToday >= maxToday;
}

/** Overdue backlog size at/above which a returning user is offered a fresh start. */
export const FRESH_START_BACKLOG = 3;
/** Days since the user's last completion at/above which they count as "away". */
export const FRESH_START_AWAY_DAYS = 7;

/**
 * A "stale" returning user: set up, *previously* active (has completed a chore
 * before), but away for a real stretch and now carrying a meaningful backlog of
 * overdue chores — the audience for the fresh-start reset.
 *
 * Absence is measured from the member's persisted last-completion date
 * (`daysAway`), not a coarse "no points this week" proxy: a user who did a chore a
 * day or two ago never left, and one stray overdue chore isn't a backlog. A
 * non-finite `daysAway` means they've never completed a chore — a brand-new user
 * in the setup flow, not a returning one — so they're excluded.
 */
export function isStale(opts: { hasHousehold: boolean; hasNoDefs: boolean; daysAway: number; overdueCount: number }): boolean {
  return (
    opts.hasHousehold &&
    !opts.hasNoDefs &&
    Number.isFinite(opts.daysAway) &&
    opts.daysAway >= FRESH_START_AWAY_DAYS &&
    opts.overdueCount >= FRESH_START_BACKLOG
  );
}
